import { type PropsWithChildren, useMemo } from "react";
import { Chip, type SxProps, type Theme } from "@mui/material";
import { lowerCase, upperFirst } from "es-toolkit/string";
import RunStatusBar from "src/business-components/RunsStatusBars/components/RunStatusBar/RunStatusBar";
import { RUN_STATUSES } from "src/business-components/RunsStatusBars/RunsStatusBars.constants";
import type { RunStatus } from "src/queries/pipelines/pipelineRuns.types";
import { RUN_STATUS_BAR_CLASS, STATUS_CHIP_SX } from "./StatusChip.constants";
import { useStatusChipStyle } from "./StatusChip.hooks";
import type { StatusChipProps } from "./StatusChip.types";

const StatusChip = ({
  children,
  label,
  status = "unknown",
  sx,
  hideLabel,
  isTask,
  runsStatus, // multiple tasks' runs status
  ref,
  ...props
}: StatusChipProps) => {
  status = status?.toLowerCase() as RunStatus;
  const style = useStatusChipStyle(status as RunStatus);

  if (label && typeof label === "string") {
    label = label.toLowerCase();
  }

  if (!label && status) {
    label = lowerCase(status);
  }

  if (typeof label === "number") {
    label = label + "";
  }

  if (status === RUN_STATUSES.COMPLETED_WITH_RETRIES) {
    status = RUN_STATUSES.COMPLETED;
    label = "+ Retries";
  }

  let finalLabel = children || upperFirst(label);

  const sxProp = useMemo(
    () =>
      ({
        ...STATUS_CHIP_SX,
        ...sx,
        ...style,
        // backgroundColor: style.backgroundColor
        //   ? style.backgroundColor
        //   : "transparent",
      }) as SxProps<Theme>,
    [sx, style]
  );

  if (!finalLabel) return null;

  if (isTask || (hideLabel && Object.values(RUN_STATUSES).includes(status))) {
    finalLabel = (
      <>
        <RunStatusBar status={status} withTooltip className={RUN_STATUS_BAR_CLASS} />
        {!hideLabel && finalLabel}
      </>
    );
  }

  return <Chip label={finalLabel} {...props} sx={sxProp} ref={ref} />;
};

// TODO: this extra component wrapper should not be in this file.
// consider using a custom tailwind classname instead of this wrapper
export const StatusChipGroup = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => (
  <div className={`flex gap-2 ${className}`}>{children}</div>
);

export default StatusChip;
